import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { nicknameAPI, nomineeAPI } from '../services/api';

const Container = styled.div`
  min-height: 100vh;
  padding: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  text-align: center;
  color: #1f2937;
  margin-bottom: 2rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  fontSize: 1rem;
  margin-bottom: 1rem;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Button = styled.button`
  background: #667eea;
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  width: 100%;
  fontWeight: 600;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #5a67d8;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #cbd5e0;
    color: #a0aec0;
    cursor: not-allowed;
    transform: none;
  }
`;

const BackButton = styled.button`
  background: #667eea;
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #5a67d8;
    transform: translateY(-1px);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  fontSize: 1rem;
  margin-bottom: 1rem;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

interface Nominee {
  id: number;
  name: string;
  room_id: number;
  creator_name: string;
}

const NicknameForm: React.FC = () => {
  const [nickname, setNickname] = useState('');
  const [selectedNominee, setSelectedNominee] = useState('');
  const [nominees, setNominees] = useState<Nominee[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const roomId = searchParams.get('room_id');

  const loadNominees = useCallback(async () => {
    if (!roomId) return;
    try {
      const nomineesData = await nomineeAPI.getRoomNominees(parseInt(roomId));
      setNominees(nomineesData);
    } catch (error) {
      console.error('Error cargando nominados:', error);
    }
  }, [roomId]);

  useEffect(() => {
    if (roomId) {
      loadNominees();
    }
  }, [roomId, loadNominees]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nickname.trim() || !selectedNominee || !roomId) {
      alert('Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      await nicknameAPI.submit({
        nickname: nickname.trim(),
        nominee_id: parseInt(selectedNominee),
        room_id: parseInt(roomId)
      });
      alert('¡Apodo guardado exitosamente!');
      navigate(`/dashboard?room_id=${roomId}`);
    } catch (error) {
      console.error('Error guardando apodo:', error);
      alert('Error al guardar apodo. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (!roomId) {
    return (
      <Container>
        <Card>
          <Title>Error</Title>
          <p>No se especificó la sala. Por favor regresa al dashboard.</p>
          <BackButton onClick={() => navigate('/dashboard')}>
            Volver al Dashboard
          </BackButton>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <BackButton onClick={() => navigate(`/dashboard?room_id=${roomId}`)}>
          ← Volver al Dashboard
        </BackButton>
        
        <Title>🏷️ Crear Apodo</Title>
        
        <form onSubmit={handleSubmit}>
          <p>Selecciona a quién le quieres poner un apodo:</p>
          
          <Select
            value={selectedNominee}
            onChange={(e) => setSelectedNominee(e.target.value)}
            required
          >
            <option value="">Selecciona un nominado</option>
            {nominees.map((nominee) => (
              <option key={nominee.id} value={nominee.id}>
                {nominee.name} (propuesto por {nominee.creator_name})
              </option>
            ))}
          </Select>
          
          <p>Propón un apodo creativo para esta persona:</p>
          
          <Input
            type="text"
            placeholder="Escribe un apodo genial..."
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            required
            maxLength={50}
          />
          
          <Button type="submit" disabled={loading || !nickname.trim() || !selectedNominee}>
            {loading ? 'Guardando...' : 'Guardar Apodo'}
          </Button>
        </form>
      </Card>
    </Container>
  );
};

export default NicknameForm;